import React from 'react';
import { Navbar, Container } from 'react-bootstrap';
import {
  BuildingStorefrontIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/solid';
import LogoDisplay from './LogoDisplay';
import { useCompanyHeader } from './hooks/useCompanyHeader';

const CompanyHeader: React.FC = () => {
  const {
    user, getSiteName, navigate, lastLoginTime, banners,
    formatLastLogin, handleLogout, isActive, mobileNavItems, handleDismissBanner,
  } = useCompanyHeader();

  return (
    <>
      {/* Top Header Bar */}
      <Navbar className="navbar-dark" style={{ backgroundColor: '#065f46', padding: '0.75rem 0' }}>
        <Container fluid>
          <Navbar.Brand
            className="d-flex align-items-center gap-2"
            onClick={() => navigate('/company/dashboard')}
            style={{ cursor: 'pointer', overflow: 'hidden', minWidth: 0, flex: '1 1 0' }}
          >
            <LogoDisplay
              type="header"
              style={{ maxWidth: '36px', maxHeight: '36px', flexShrink: 0 }}
              fallbackIcon={<BuildingStorefrontIcon style={{ width: '24px', height: '24px', color: 'white' }} />}
              alt={`${getSiteName()} Logo`}
            />
            <span className="header-brand-text" style={{
              color: 'white', fontSize: '1.1rem', fontWeight: '600',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              minWidth: 0,
            }}>{getSiteName()}</span>
          </Navbar.Brand>

          {/* Mobile: just show logout button in header */}
          <div className="d-lg-none">
            <button
              onClick={handleLogout}
              style={{
                background: 'transparent',
                border: '1.5px solid rgba(255,255,255,0.4)',
                borderRadius: '8px',
                color: 'white',
                padding: '6px 12px',
                fontSize: '0.8rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <ArrowRightOnRectangleIcon style={{ width: '16px', height: '16px' }} />
              Logout
            </button>
          </div>
        </Container>
      </Navbar>

      {/* Company Info Bar */}
      {user && user.role === 'company' && (
        <div style={{
          backgroundColor: '#047857',
          padding: '6px 0',
          fontSize: '0.8rem',
          color: 'rgba(255,255,255,0.85)',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Container fluid className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2" style={{ overflow: 'hidden', minWidth: 0 }}>
              <UserCircleIcon style={{ width: '16px', height: '16px', color: 'rgba(255,255,255,0.7)', flexShrink: 0 }} />
              <span style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {user.company_name || user.username}
              </span>
              {user.is_company_admin && !user.is_company_owner && (
                <>
                  <span style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>|</span>
                  <span style={{ fontWeight: '400', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, fontSize: '0.78rem' }}>
                    {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username}
                  </span>
                </>
              )}
              <span style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                padding: '1px 8px',
                borderRadius: '10px',
                fontSize: '0.75rem',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}>
                {user.is_company_admin && !user.is_company_owner ? 'Team Admin' : 'Company Manager'}
              </span>
            </div>
            <div className="d-none d-sm-flex align-items-center gap-2">
              <ClockIcon style={{ width: '14px', height: '14px', color: 'rgba(255,255,255,0.6)' }} />
              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
                Last login: {formatLastLogin(lastLoginTime || user.last_login)}
              </span>
            </div>
          </Container>
        </div>
      )}

      {/* Admin-configured Notification Banners */}
      {banners.map((banner) => (
        <div key={banner.id} style={{
          background: banner.background_color,
          padding: '9px 0',
          fontSize: '0.84rem',
          color: banner.text_color,
          textAlign: 'center',
          fontWeight: '600',
        }}>
          <Container fluid className="d-flex justify-content-center align-items-center gap-2 flex-wrap">
            <span>{banner.message}</span>
            {banner.button_text && (
              <button
                type="button"
                onClick={() => { if (banner.button_url) { /^https?:\/\//.test(banner.button_url) ? window.open(banner.button_url, '_blank', 'noopener') : navigate(banner.button_url); } }}
                style={{
                  backgroundColor: banner.button_bg_color || 'rgba(255,255,255,0.25)',
                  color: banner.button_text_color || banner.text_color,
                  padding: '2px 12px',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  cursor: banner.button_url ? 'pointer' : 'default',
                  marginLeft: '4px',
                  border: 'none',
                  lineHeight: 'inherit',
                  fontWeight: 'inherit',
                }}
              >
                {banner.button_text}
              </button>
            )}
            {(banner.is_dismissible || banner.trigger_type === 'first_login') && (
              <button
                type="button"
                onClick={() => handleDismissBanner(banner.id)}
                style={{
                  cursor: 'pointer',
                  marginLeft: '8px',
                  opacity: 0.7,
                  fontSize: '1rem',
                  lineHeight: 1,
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  padding: 0,
                }}
                title="Dismiss"
                aria-label="Dismiss banner"
              >
                ✕
              </button>
            )}
          </Container>
        </div>
      ))}

      {/* Trial / Subscription Banner */}
      {user && user.role === 'company' && user.subscription_tier === 'free' && user.is_expired && (
        <div style={{
          background: 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)',
          padding: '10px 0',
          fontSize: '0.85rem',
          color: 'white',
          textAlign: 'center',
          fontWeight: '600',
        }}>
          <Container fluid className="d-flex justify-content-center align-items-center gap-2">
            <ExclamationTriangleIcon style={{ width: '18px', height: '18px' }} />
            <span>Your free trial has expired — account is in read-only mode</span>
            <button type="button" onClick={() => navigate('/pricing')} style={{
              backgroundColor: 'rgba(255,255,255,0.25)',
              color: 'inherit',
              padding: '2px 12px',
              borderRadius: '10px',
              fontSize: '0.75rem',
              cursor: 'pointer',
              marginLeft: '4px',
              border: 'none',
              fontWeight: 'inherit',
            }}>
              Upgrade Now
            </button>
          </Container>
        </div>
      )}

      {user && user.role === 'company' && user.subscription_tier === 'free' && !user.is_expired && user.days_remaining !== null && user.days_remaining !== undefined && (
        <div style={{
          background: user.days_remaining <= 3 
            ? 'linear-gradient(90deg, #dc2626 0%, #ef4444 100%)'
            : user.days_remaining <= 7
              ? 'linear-gradient(90deg, #d97706 0%, #f59e0b 100%)'
              : 'linear-gradient(90deg, #2563eb 0%, #3b82f6 100%)',
          padding: '8px 0',
          fontSize: '0.82rem',
          color: 'white',
          textAlign: 'center',
          fontWeight: '500',
        }}>
          <Container fluid className="d-flex justify-content-center align-items-center gap-2">
            <ExclamationTriangleIcon style={{ width: '16px', height: '16px' }} />
            <span>
              {user.days_remaining === 0
                ? 'Your free trial expires today!'
                : `Free trial: ${user.days_remaining} day${user.days_remaining !== 1 ? 's' : ''} remaining`}
            </span>
            <button type="button" onClick={() => navigate('/pricing')} style={{
              backgroundColor: 'rgba(255,255,255,0.25)',
              color: 'inherit',
              padding: '2px 10px',
              borderRadius: '10px',
              fontSize: '0.75rem',
              cursor: 'pointer',
              marginLeft: '4px',
              border: 'none',
              fontWeight: 'inherit',
            }}>
              Upgrade
            </button>
          </Container>
        </div>
      )}

      {/* Mobile Bottom Navigation Bar */}
      <nav className="mobile-bottom-nav d-lg-none" style={{ backgroundColor: '#065f46' }}>
        {mobileNavItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`bottom-nav-item ${active ? 'active' : ''}`}
              style={{ color: active ? '#ffffff' : 'rgba(255,255,255,0.7)' }}
            >
              <Icon style={{ width: '20px', height: '20px' }} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
};

export default CompanyHeader;
